import React, { useReducer } from 'react';
import { FaPauseCircle } from 'react-icons/fa';
import { Icon } from '@rsuite/icons';
import { Popover, Whisper } from 'rsuite';
import styled from '@emotion/styled';
import ProductHoldDialog from './ProductHoldDialog';
import ProductUnholdDialog from './ProductUnholdDialog';

interface Props {
  offerId: number;
  isOnHold: boolean;
  holdReason?: string;
  marketplace: string;
}

interface HoldIconProps {
  isOnHold: boolean;
}

const HoldIcon = styled(Icon, {
  shouldForwardProp: prop => prop !== 'isOnHold',
})<HoldIconProps>({
  fontSize: 20,
  display: 'block',
}, props => ({
  color: props.isOnHold ? '#ffa500' : '#c0c4cc',
}));

const HoldButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: none;
    line-height: 0;
    cursor: pointer;
`;

type Dialog = 'hold' | 'unhold';

interface State {
  openDialog?: Dialog;
}

const initialState: State = {
  openDialog: undefined,
};

type DialogOpenAction = { type: 'dialog/open', payload: Dialog };
type DialogCloseAction = { type: 'dialog/close' };
type Action = DialogOpenAction | DialogCloseAction;

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'dialog/open':
      return { ...state, openDialog: action.payload };
    case 'dialog/close':
      return { ...state, openDialog: undefined };
    default:
      return state;
  }
}

function getTooltipMessage(
  isOnHold: boolean,
  marketplace: string,
  holdReason?: string,
): string {
  if (!isOnHold) {
    return `Click to put the offer on hold and hide it from ${marketplace}.`;
  }

  return `Offer is on hold and hidden from ${marketplace} due to ${holdReason}. Click to unhold it.`;
}

export default function ProductHoldButton({
  offerId, isOnHold, holdReason, marketplace,
}: Props): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);
  const message = getTooltipMessage(isOnHold, marketplace, holdReason);

  function handleClick(): void {
    dispatch({ type: 'dialog/open', payload: isOnHold ? 'unhold' : 'hold' });
  }

  function handleClose(): void {
    dispatch({ type: 'dialog/close' });
  }

  return (
    <>
      <Whisper
        speaker={<Popover arrow={false}>{message}</Popover>}
        trigger="hover"
        placement="bottomStart"
        preventOverflow
      >
        <HoldButton
          type="button"
          onClick={() => handleClick()}
        >
          <HoldIcon as={FaPauseCircle} isOnHold={isOnHold} />
        </HoldButton>
      </Whisper>
      {state.openDialog === 'hold' && (
        <ProductHoldDialog
          offerId={offerId}
          marketplace={marketplace}
          close={() => handleClose()}
        />
      )}
      {state.openDialog === 'unhold' && (
        <ProductUnholdDialog
          offerId={offerId}
          holdReason={holdReason}
          marketplace={marketplace}
          close={() => handleClose()}
        />
      )}
    </>
  );
}

ProductHoldButton.defaultProps = {
  holdReason: undefined,
};
